import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Package, Calculator } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../seo/SEO';
import { track } from '../lib/analytics';

/**
 * Custom 404. Mounted as the catch-all route in App.js so any URL that
 * doesn't match a real route lands here (instead of a blank screen). We
 * also push a `page_not_found` GTM event with the bad path so we can see
 * which broken inbound links are hitting the site.
 *
 * SEO: explicitly noindex so Google doesn't waste crawl budget on
 * misspelled URLs.
 */
export default function NotFound() {
  useEffect(() => {
    track('page_not_found', {
      missing_path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, []);

  const suggestions = [
    {
      icon: Package,
      title: 'How It Works',
      description: 'See our 3PL fulfillment process step-by-step.',
      to: '/#how-it-works',
    },
    {
      icon: Calculator,
      title: 'Savings Calculator',
      description: 'Find out how much you could save vs. your current 3PL.',
      to: '/#calculator',
    },
    {
      icon: MapPin,
      title: 'Shipping Zones',
      description: 'See transit times to every U.S. state from our Florida hub.',
      to: '/zones',
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <SEO
        title="Page not found"
        description="The page you were looking for doesn't exist. Browse popular pages or get a custom 3PL fulfillment quote from Shipmaster."
        noindex={true}
      />
      <Header />

      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 w-full">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-widest text-indigo-600">
            404 — Page not found
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
            That route went off the map.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            The page you&apos;re looking for doesn&apos;t exist or has moved. Try one of the
            popular destinations below, or jump straight to a quote.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              data-testid="not-found-home-btn"
              className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            >
              Back to home
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              data-testid="not-found-contact-btn"
              className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 text-slate-800 font-semibold transition-all duration-200"
            >
              Get a custom quote
            </Link>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {suggestions.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.title}
                to={s.to}
                className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                  <Icon className="w-5 h-5 text-indigo-600" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
              </Link>
            );
          })}
        </div>
      </main>

      <Footer />
    </div>
  );
}
